import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  Cancel as CancelIcon,
  HelpOutline as HelpOutlineIcon
} from '@mui/icons-material';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Predefined fact-checking questions matching Python AI fact-checker format
const predefinedQuestions = [
  {
    id: 'accuracy',
    category: 'Accuracy of Quoted Numbers',
    fact: 'Are the figures consistent with the latest official data (e.g., World Bank, IMF, national statistics office)?',
    context: 'Is the number rounded, adjusted, or cherry-picked in a way that could mislead?',
  },
  {
    id: 'timeliness',
    category: 'Timeliness of the Data',
    fact: 'Is the article citing the most recent available dataset?',
    context: 'Does the article make it clear if the numbers are from last year, last quarter, or an older benchmark?',
  },
  {
    id: 'units',
    category: 'Comparability & Units',
    fact: 'Are the numbers presented in correct units?',
    context: 'Does the comparison across countries, years, or sectors make sense once adjusted for inflation, currency, or population?',
  },
  {
    id: 'sources',
    category: 'Source Reliability',
    fact: 'Is the quoted statistic traceable to a reliable source (central bank, regulator, international organization)?',
    context: 'Has the content clarified the source\'s methodology, and is it aligned with standard economic definitions?',
  },
  {
    id: 'consistency',
    category: 'Consistency With Related Indicators',
    fact: 'Do other official indicators support the quoted figure?',
    context: 'Does the claim logically fit with broader trends?',
  },
];

const FactCheckWidget = ({ articleContent }) => {
  const location = useLocation();
  const [selectedChecks, setSelectedChecks] = useState([]);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentUrl, setCurrentUrl] = useState('');
  const [isArticlePage, setIsArticlePage] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const url = window.location.href;
    const pathname = window.location.pathname;
    setCurrentUrl(url);
    // Check if we're on an article page (has /post/ in path)
    const isArticle = pathname.includes('/post/') || url.includes('/post/');
    setIsArticlePage(isArticle);
    console.log('FactCheckWidget - URL:', url, 'Pathname:', pathname, 'isArticlePage:', isArticle);
  }, [location]);

  const handleCheckToggle = (checkId) => {
    setSelectedChecks((prev) =>
      prev.includes(checkId)
        ? prev.filter((id) => id !== checkId)
        : [...prev, checkId]
    );
  };

  // Helper function to clean and optimize HTML
  const cleanHTML = (html) => {
    if (!html) return '';
    
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    const elementsToRemove = tempDiv.querySelectorAll('script, style, noscript, iframe, nav, header, footer, aside, .sidebar, .navigation, .menu, .advertisement, .ad, [class*="ad"], [id*="ad"]');
    elementsToRemove.forEach(el => el.remove());
    
    let cleaned = tempDiv.innerHTML;
    cleaned = cleaned.replace(/\s+/g, ' ').trim();
    
    const maxLength = 50000;
    if (cleaned.length > maxLength) {
      cleaned = cleaned.substring(0, maxLength) + '...[truncated]';
    }
    
    return cleaned;
  };

  const handleRunFactCheck = async () => {
    if (!selectedChecks.length) {
      setError('Please select at least one check to run');
      return;
    }

    if (!isArticlePage) {
      setError('Please visit an article page to run fact-check');
      return;
    }

    setIsLoading(true);
    setError('');

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: `Fact-check: ${predefinedQuestions.filter(q => selectedChecks.includes(q.id)).map(q => q.category).join(', ')}`,
      category: 'Fact Check Request'
    };

    setMessages([userMessage]);

    try {
      // Use the articleContent prop if provided, otherwise fall back to DOM parsing
      let pageText = articleContent || 
                     document.querySelector('.post-content')?.innerHTML || 
                     document.querySelector('article')?.innerHTML || 
                     document.querySelector('[class*="post"]')?.innerHTML ||
                     document.querySelector('[class*="content"]')?.innerHTML ||
                     null;
      
      if (!pageText || pageText.trim().length === 0) {
        throw new Error('Could not extract article content. Please ensure you are on an article page.');
      }
      
      pageText = cleanHTML(pageText);

      const response = await fetch(`${apiUrl}/ai/factcheck`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pageText, pageUrl: currentUrl, selectedChecks })
      });

      if (!response.ok) {
        let errorMessage = 'Failed to process fact-check request';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
          if (errorData.details) errorMessage += `: ${errorData.details}`;
        } catch (e) {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error + (data.details ? `: ${data.details}` : ''));
      }
      
      let structuredData = null;
      
      if (data.structured) {
        structuredData = data.structured;
      } else if (data.summary || data.items || data.verdict || data.title) {
        structuredData = data;
      } else if (data.text) {
        try {
          const parsed = JSON.parse(data.text);
          structuredData = parsed;
        } catch (e) {
          structuredData = {
            summary: data.text,
            title: 'Fact-Check Results',
            verdict: 'uncertain'
          };
        }
      }
      
      if (!structuredData) {
        throw new Error('Invalid response format from fact-checker service');
      }
      
      const hasContent = structuredData.summary || structuredData.items || structuredData.text || structuredData.title || structuredData.verdict;
      if (!hasContent) {
        structuredData = {
          ...structuredData,
          summary: JSON.stringify(structuredData, null, 2)
        };
      }

      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: structuredData.summary || structuredData.text || structuredData.title || 'Fact-check complete',
        category: 'Fact Check Results',
        structuredData: structuredData
      };

      setMessages(prev => [...prev, aiMessage]);
      setSelectedChecks([]);
      setIsExpanded(true); // Expand to show results
    } catch (error) {
      console.error('Error processing fact-check:', error);
      setError(error.message || 'Failed to process fact-check. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const clearChat = () => {
    setMessages([]);
    setError('');
    setSelectedChecks([]);
    setIsExpanded(false);
    setIsLoading(false);
  };

  const getVerdictIcon = (verdict) => {
    switch (verdict?.toLowerCase()) {
      case 'supported':
        return <CheckCircleIcon sx={{ color: '#4caf50' }} />;
      case 'uncertain':
        return <WarningIcon sx={{ color: '#ff9800' }} />;
      case 'contradicted':
        return <CancelIcon sx={{ color: '#f44336' }} />;
      default:
        return <HelpOutlineIcon sx={{ color: '#9e9e9e' }} />;
    }
  };

  const getVerdictColor = (verdict) => {
    switch (verdict?.toLowerCase()) {
      case 'supported':
        return '#4caf50';
      case 'uncertain':
        return '#ff9800';
      case 'contradicted':
        return '#f44336';
      default:
        return '#9e9e9e';
    }
  };

  const renderStructuredResponse = (data) => {
    if (!data) {
      return <Typography variant="body2" color="text.secondary">No data to display</Typography>;
    }

    let parsedData = data;
    if (data.text && typeof data.text === 'string' && !data.summary && !data.items) {
      try {
        const parsed = JSON.parse(data.text);
        parsedData = { ...data, ...parsed };
      } catch (e) {
        if (!parsedData.summary) {
          parsedData.summary = data.text;
        }
      }
    }

    return (
      <Box sx={{ mt: 0, width: '100%' }}>
        {parsedData.summary && (
          <Paper sx={{ p: 2, mb: 2, backgroundColor: '#e3f2fd', borderLeft: '4px solid #2196f3' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, fontSize: '1rem' }}>
              Summary
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>
              {parsedData.summary}
            </Typography>
          </Paper>
        )}

        {parsedData.items && Array.isArray(parsedData.items) && parsedData.items.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, fontSize: '1rem' }}>
              Detailed Analysis ({parsedData.items.length} item{parsedData.items.length !== 1 ? 's' : ''})
            </Typography>
            {parsedData.items.map((item, index) => (
              <Accordion key={item.id || `item-${index}`} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    {getVerdictIcon(item.verdict)}
                    <Chip
                      label={item.verdict?.toUpperCase()}
                      sx={{
                        backgroundColor: getVerdictColor(item.verdict),
                        color: 'white',
                        fontWeight: 'bold',
                        mr: 1,
                        fontSize: '0.7rem',
                        height: '24px'
                      }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                      {item.title}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    {item.explanation && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line', fontSize: '0.875rem' }}>
                          {item.explanation}
                        </Typography>
                      </Box>
                    )}
                    {item.confidence !== undefined && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                          Confidence: {Math.round(item.confidence * 100)}%
                        </Typography>
                      </Box>
                    )}
                    {item.suggested_sources && item.suggested_sources.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, fontSize: '0.875rem' }}>
                          Suggested Sources:
                        </Typography>
                        <List dense>
                          {item.suggested_sources.map((source, idx) => (
                            <ListItem key={idx} sx={{ py: 0.5 }}>
                              <ListItemText
                                primary={source}
                                primaryTypographyProps={{ variant: 'body2', fontSize: '0.75rem' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  // Always render the widget (it will check internally if on article page)
  // This allows it to show on any page, but the internal logic handles article page detection
  console.log('FactCheckWidget render - isArticlePage:', isArticlePage, 'factCheckEnabled:', true); // Debug
  
  return (
    <Card sx={{ mb: 4, borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ bgcolor: '#1976d2', py: 2, px: 3 }}>
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
          A. I. Facts Check
        </Typography>
      </Box>
      <CardContent>
        {!isArticlePage && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Visit an article page to use the fact-checker
          </Alert>
        )}
        {messages.length === 0 ? (
          <>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mb: 1, fontSize: '0.9rem' }}>
              Select Fact-Check Criteria:
            </Typography>
            
            <FormGroup sx={{ gap: 0.5 }}>
              {predefinedQuestions.map((question) => (
                <FormControlLabel
                  key={question.id}
                  control={
                    <Checkbox
                      checked={selectedChecks.includes(question.id)}
                      onChange={() => handleCheckToggle(question.id)}
                      disabled={!isArticlePage}
                      size="small"
                    />
                  }
                  label={
                    <Box sx={{ ml: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>
                        {question.category}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem', lineHeight: 1.3 }}>
                        a) {question.fact}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem', lineHeight: 1.3 }}>
                        b) {question.context}
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', mb: 0.5, py: 0.5 }}
                />
              ))}
            </FormGroup>
            
            {error && (
              <Alert severity="error" sx={{ mb: 1, mt: 1 }} onClose={() => setError('')}>
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold' }}>
                  {error}
                </Typography>
              </Alert>
            )}
            
            <Button
              fullWidth
              variant="contained"
              onClick={handleRunFactCheck}
              disabled={isLoading || selectedChecks.length === 0}
              startIcon={isLoading ? <CircularProgress size={20} /> : <SendIcon />}
              sx={{ mt: 2, mb: 1 }}
            >
              {isLoading ? 'Analyzing...' : 'Run Fact Check'}
            </Button>
          </>
        ) : (
          <>
            <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={clearChat}
                sx={{ flex: 1 }}
              >
                Reset & Start New Check
              </Button>
            </Box>

            <Box sx={{ maxHeight: isExpanded ? 'none' : '400px', overflowY: 'auto' }}>
              {messages.map((message) => (
                <Box
                  key={message.id}
                  sx={{
                    mb: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: message.type === 'user' ? 'flex-end' : 'flex-start'
                  }}
                >
                  {message.type === 'user' && (
                    <Chip
                      label={message.category}
                      size="small"
                      sx={{ mb: 0.5, alignSelf: 'flex-end' }}
                    />
                  )}
                  <Paper
                    sx={{
                      p: 2,
                      maxWidth: message.type === 'user' ? '80%' : '100%',
                      backgroundColor: message.type === 'user' ? 'primary.main' : 'white',
                      color: message.type === 'user' ? 'white' : 'text.primary',
                      width: message.type === 'ai' ? '100%' : 'auto'
                    }}
                  >
                    {message.type === 'user' ? (
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {message.content}
                      </Typography>
                    ) : (
                      message.structuredData ? (
                        renderStructuredResponse(message.structuredData)
                      ) : (
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          {message.content}
                        </Typography>
                      )
                    )}
                  </Paper>
                </Box>
              ))}

              {isLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">
                    AI is analyzing...
                  </Typography>
                </Box>
              )}

              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    Error:
                  </Typography>
                  <Typography variant="body2">
                    {error}
                  </Typography>
                </Alert>
              )}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default FactCheckWidget;

